import {
  AttendanceCustomTimeRangeDayEnum,
  AttendanceEnumsPoolTypeEnum,
  PayrollConceptFragment,
  WorkTimeCategoriesCompensationsReimbursablePayrollConceptFragment,
  WorkTimeCategoriesContractTaxonomyFragment,
  WorkTimeCategoriesReimbursableSupplementTaxonomyFragment,
} from 'generated/resources/graphql'

import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

export interface ReimbursableTaxonomiesMappingForm {
  id?: string
  supplementTaxonomyId: string
  legalEntityId: string
  timeRangeCategoryId: string
}

type SlotSelectEnum = 'full-day' | 'specific-time-slot'

export interface CustomTimeRangesFormData {
  id?: string | undefined
  attendance_custom_time_range_category_id: string
  time_settings_custom_time_range_category_id: string
  day: AttendanceCustomTimeRangeDayEnum[]
  pool_type?: AttendanceEnumsPoolTypeEnum
  start_time?: string
  end_time?: string
  attendance_employees_setting_id: string
  reimbursable_taxonomies_mappings?: ReimbursableTaxonomiesMappingForm[]
}

export interface WizardData {
  id?: string | undefined
  timeSettingsCustomTimeRangeCategoryId?: number
  day?: AttendanceCustomTimeRangeDayEnum[]
  date?: string
  poolType: AttendanceEnumsPoolTypeEnum
  rawMinutes: number
  equivalentMinutes: number
  startTime?: string
  endTime?: string
  slotSelected: SlotSelectEnum
  payrollEnabled: boolean
  errors?: {
    [id: string]: string
  }
  attendanceEmployeesSettingId: string
  contractTaxonomies: WorkTimeCategoriesContractTaxonomyFragment[]
  payrollConcepts?: PayrollConceptFragment[] | null
  formMappingsTaxonomies: ReimbursableTaxonomiesMappingForm[]
  payrollConceptId?: number | null
  payrollMappingsTaxonomies: WorkTimeCategoriesReimbursableSupplementTaxonomyFragment[]
  payrollMappingsCompensations?:
    | WorkTimeCategoriesCompensationsReimbursablePayrollConceptFragment[]
    | null
  canCreateSettingCategory: boolean
  hasCompensations?: boolean
  isEdit: boolean
  hasInvalidMultiplier: () => boolean
}

export type WizardDataField = keyof WizardData

const validateMandatory = (field: WizardDataField, data: WizardData) => {
  const value = data[field]
  if (Array.isArray(value)) {
    return !!value.length
  }
  return !!data[field]
}

const getInvalidMandatoryFields = (mandatoryFields: WizardDataField[], data: WizardData) => {
  const allMandatoryFields = [...mandatoryFields]
  // Only validate payrollConceptId when hasCompensations is true (PayrollConceptSelect is shown)
  // When hasCompensations is false, PayrollMappings component is used instead
  if (
    mandatoryFields.includes('poolType') &&
    data.poolType === 'virtual_payroll' &&
    data.hasCompensations
  ) {
    allMandatoryFields.push('payrollConceptId')
  }
  return allMandatoryFields
    .map((field) => {
      return validateMandatory(field, data)
        ? null
        : { [field]: i18n.t('time_tracking.settings.work_time_categories.forms.errors.mandatory') }
    })
    .filter(Boolean)
}

const validateMultiplierMinutes = (data: WizardData) => {
  if (data.rawMinutes === 0)
    return {
      rawMinutes: i18n.t(
        'time_tracking.settings.work_time_categories.forms.errors.zero_multiplier'
      ),
      equivalentMinutes: '',
    }

  if (data.equivalentMinutes === 0)
    return {
      rawMinutes: '',
      equivalentMinutes: i18n.t(
        'time_tracking.settings.work_time_categories.forms.errors.zero_multiplier'
      ),
    }

  if (data.hasInvalidMultiplier()) {
    return {
      rawMinutes: '',
      equivalentMinutes: `${i18n.t(
        'time_tracking.settings.work_time_categories.forms.errors.multiplier'
      )} ${minutesToText(data.rawMinutes)}`,
    }
  }
  return null
}

export const validateMandatoryFields = (mandatoryFields: WizardDataField[], data: WizardData) => {
  const invalidFields = getInvalidMandatoryFields(mandatoryFields, data)
  const mutlipliersError = validateMultiplierMinutes(data)

  if (mutlipliersError) {
    invalidFields.push(mutlipliersError)
  }

  const getDataWithErrors = () => {
    const errors = Object.assign({}, ...invalidFields)

    return {
      ...data,
      errors,
    }
  }
  return {
    dataWithErrors: getDataWithErrors(),
    invalidFields,
  }
}
